import AMapLoader from '@amap/amap-jsapi-loader'
import { Loader } from '@googlemaps/js-api-loader'
import store from '@/store'

let globalAMap = null
let globalGoogleMap = null

class MapAdapter {
  get mapType () {
    return (store.state.mapConfig && store.state.mapConfig.map_type) ? store.state.mapConfig.map_type : 0
  }

  get amapKey () {
    return (store.state.mapConfig && store.state.mapConfig.amap_key) ? store.state.mapConfig.amap_key : ''
  }

  get googleKey () {
    return (store.state.mapConfig && store.state.mapConfig.google_map_key) ? store.state.mapConfig.google_map_key : ''
  }

  get isAmap () {
    return this.mapType === 0
  }

  get isGoogle () {
    return this.mapType === 1
  }

  async init (plugins = []) {
    if (this.isAmap) {
      if (!globalAMap) {
        try {
          globalAMap = await AMapLoader.load({
            key: this.amapKey,
            version: '2.0',
            plugins: plugins
          })
        } catch (e) {
          console.error('Failed to load AMap:', e)
          throw e
        }
      }
      return globalAMap
    } else {
      if (!globalGoogleMap) {
        try {
          const loader = new Loader({
            apiKey: this.googleKey || '', // 防止由于没有key导致loader初始化失败
            version: 'weekly',
            libraries: ['places', 'drawing', 'geometry', 'marker']
          })
          globalGoogleMap = await loader.load()
        } catch (e) {
          console.error('Failed to load Google Maps:', e)
          // 确保报错时也不要中断其他操作，至少给个空对象以便后续 null-check 拦截
          globalGoogleMap = { maps: null }
          throw e
        }
      }
      return globalGoogleMap
    }
  }

  createMap (container, options) {
    if (this.isAmap) {
      if (!globalAMap) {
        console.warn('AMap API not loaded properly')
        return null
      }
      return new globalAMap.Map(container, {
        zoom: options.zoom || 5,
        center: options.center ? [options.center.lng, options.center.lat] : undefined,
        mapStyle: options.mapStyle,
        viewMode: options.viewMode,
        pitch: options.pitch
      })
    } else {
      if (!globalGoogleMap || !globalGoogleMap.maps) {
        console.warn('Google Maps API not loaded properly')
        return null
      }
      return new globalGoogleMap.maps.Map(typeof container === 'string' ? document.getElementById(container) : container, {
        zoom: options.zoom || 5,
        center: options.center ? { lat: options.center.lat, lng: options.center.lng } : { lat: 39.9, lng: 116.39 },
        mapId: 'DEMO_MAP_ID', // required for AdvancedMarkerElement
        disableDefaultUI: options.disableDefaultUI
      })
    }
  }

  createMarker (options) {
    if (this.isAmap) {
      if (!globalAMap) return null
      return new globalAMap.Marker({
        position: new globalAMap.LngLat(options.position.lng, options.position.lat),
        offset: options.offset ? new globalAMap.Pixel(options.offset.x, options.offset.y) : undefined,
        content: options.content,
        icon: options.icon ? new globalAMap.Icon(options.icon) : undefined,
        extData: options.extData
      })
    } else {
      if (!globalGoogleMap || !globalGoogleMap.maps || !globalGoogleMap.maps.marker) return null

      // Google AdvancedMarkerElement
      let content = null
      if (options.content) {
        if (typeof options.content === 'string') {
          const div = document.createElement('div')
          div.innerHTML = options.content.trim()
          content = div.firstChild
        } else {
          content = options.content
        }
      } else if (options.icon) {
        const img = document.createElement('img')
        img.src = options.icon.image
        if (options.icon.imageSize) {
          img.style.width = options.icon.imageSize.width + 'px'
          img.style.height = options.icon.imageSize.height + 'px'
        }
        content = img
      }

      const collisionBehavior = globalGoogleMap.maps.marker && globalGoogleMap.maps.marker.CollisionBehavior
        ? globalGoogleMap.maps.marker.CollisionBehavior.OPTIONAL_AND_HIDES_LOWER_PRIORITY : undefined

      // Apply offset for Google AdvancedMarkerElement by wrapping content
      let finalContent = content
      if (options.offset && content) {
        const wrapper = document.createElement('div')
        wrapper.style.width = '0px'
        wrapper.style.height = '0px'
        wrapper.style.position = 'absolute'
        wrapper.style.left = options.offset.x + 'px'
        wrapper.style.top = options.offset.y + 'px'
        wrapper.appendChild(content)
        finalContent = wrapper
      }

      const marker = new globalGoogleMap.maps.marker.AdvancedMarkerElement({
        position: { lat: Number(options.position.lat), lng: Number(options.position.lng) },
        content: finalContent,
        collisionBehavior: collisionBehavior
      })
      if (options.extData) {
        marker.extData = options.extData
      }

      // 代理 on 事件 (使用 Proxy 避免覆盖 Google Maps 内部混淆的 on 属性)
      const customOn = (eventName, callback) => {
        let evt = eventName === 'mouseover' ? 'pointerenter' : (eventName === 'mouseout' ? 'pointerleave' : eventName)
        marker.addListener(evt, callback)
        if (content && typeof content.addEventListener === 'function') {
          content.addEventListener(eventName, callback)
        }
      }
      this._safeDefineOn(marker, customOn)
      marker.setPosition = (pos) => {
        let lat, lng
        if (Array.isArray(pos)) {
          lat = Number(pos[1])
          lng = Number(pos[0])
        } else {
          lat = Number(pos.lat || (typeof pos.getLat === 'function' && pos.getLat()))
          lng = Number(pos.lng || (typeof pos.getLng === 'function' && pos.getLng()))
        }
        marker.position = { lat, lng }
      }
      return marker
    }
  }

  createInfoWindow (options) {
    if (this.isAmap) {
      if (!globalAMap) return null
      return new globalAMap.InfoWindow({
        content: options.content,
        offset: options.offset ? new globalAMap.Pixel(options.offset.x, options.offset.y) : undefined,
        closeWhenClickMap: options.closeWhenClickMap
      })
    } else {
      if (!globalGoogleMap || !globalGoogleMap.maps) return null
      let contentNode = null
      if (options.content) {
        if (typeof options.content === 'string') {
          const div = document.createElement('div')
          div.innerHTML = options.content.trim()
          contentNode = div.firstChild
        } else {
          contentNode = options.content
        }
      }
      const infoWindow = new globalGoogleMap.maps.InfoWindow({
        content: contentNode,
        pixelOffset: options.offset ? new globalGoogleMap.maps.Size(options.offset.x, options.offset.y) : undefined
      })
      const openOrig = infoWindow.open.bind(infoWindow)
      const setContentOrig = infoWindow.setContent.bind(infoWindow)

      // 代理 on 事件 (使用 Proxy 避免覆盖 Google Maps 内部混淆的 on 属性)
      const customOn = (eventName, callback) => {
        if (contentNode && typeof contentNode.addEventListener === 'function') {
          contentNode.addEventListener(eventName, callback)
        }
      }
      this._safeDefineOn(infoWindow, customOn)
      infoWindow.open = (map, anchor) => {
        if (anchor && (anchor.lat || anchor.latLng || Array.isArray(anchor) || (typeof anchor.getLat === 'function'))) {
          // If anchor is a coordinate, set position
          let lat, lng
          if (Array.isArray(anchor)) {
            lat = Number(anchor[1])
            lng = Number(anchor[0])
          } else if (typeof anchor.getLat === 'function') {
            lat = Number(anchor.getLat())
            lng = Number(anchor.getLng())
          } else {
            lat = Number(anchor.lat)
            lng = Number(anchor.lng)
          }
          infoWindow.setPosition({ lat, lng })
          openOrig(map)
        } else {
          openOrig({
            anchor: anchor,
            map
          })
        }
      }
      infoWindow.setContent = (content) => {
        if (typeof content === 'string') {
          const div = document.createElement('div')
          div.innerHTML = content.trim()
          contentNode = div.firstChild
          setContentOrig(contentNode)
        } else {
          contentNode = content
          setContentOrig(content)
        }
      }
      return infoWindow
    }
  }

  createPolyline (options) {
    if (this.isAmap) {
      if (!globalAMap) return null
      return new globalAMap.Polyline({
        path: options.path.map(p => new globalAMap.LngLat(Number(p.lng), Number(p.lat))),
        showDir: options.showDir,
        strokeColor: options.strokeColor,
        strokeWeight: options.strokeWeight,
        strokeOpacity: options.strokeOpacity
      })
    } else {
      if (!globalGoogleMap || !globalGoogleMap.maps) return null
      const poly = new globalGoogleMap.maps.Polyline({
        path: options.path.map(p => ({ lat: Number(p.lat), lng: Number(p.lng) })),
        strokeColor: options.strokeColor,
        strokeWeight: options.strokeWeight,
        strokeOpacity: options.strokeOpacity,
        icons: options.showDir ? [{
          icon: { path: globalGoogleMap.maps.SymbolPath.FORWARD_CLOSED_ARROW },
          offset: '100%',
          repeat: '50px'
        }] : []
      })
      const setPathOrig = poly.setPath.bind(poly)
      poly.setPath = (path) => {
        setPathOrig(path.map(p => ({ lat: p.lat, lng: p.lng })))
      }
      return poly
    }
  }

  createCircle (options) {
    if (this.isAmap) {
      if (!globalAMap) return null
      return new globalAMap.Circle({
        center: new globalAMap.LngLat(Number(options.center.lng), Number(options.center.lat)),
        radius: options.radius,
        strokeColor: options.strokeColor || '#2D8CF0',
        strokeWeight: options.strokeWeight || 2,
        strokeOpacity: options.strokeOpacity || 0.5,
        fillColor: options.fillColor || '#2D8CF0',
        fillOpacity: options.fillOpacity || 0.2
      })
    } else {
      if (!globalGoogleMap || !globalGoogleMap.maps) return null
      const circle = new globalGoogleMap.maps.Circle({
        center: { lat: Number(options.center.lat), lng: Number(options.center.lng) },
        radius: options.radius,
        strokeColor: options.strokeColor || '#2D8CF0',
        strokeWeight: options.strokeWeight || 2,
        strokeOpacity: options.strokeOpacity || 0.5,
        fillColor: options.fillColor || '#2D8CF0',
        fillOpacity: options.fillOpacity || 0.2
      })
      // 代理高德的方法
      const setCenterOrig = circle.setCenter.bind(circle)
      const setRadiusOrig = circle.setRadius.bind(circle)
      circle.setCenter = (center) => setCenterOrig({ lat: center.lat, lng: center.lng })
      circle.setRadius = (r) => setRadiusOrig(r)
      return circle
    }
  }

  // Polygon
  createPolygon (options) {
    if (this.isAmap) {
      if (!globalAMap) return null
      return new globalAMap.Polygon({
        path: options.path.map(p => new globalAMap.LngLat(Number(p.lng), Number(p.lat))),
        strokeColor: options.strokeColor || '#2D8CF0',
        strokeWeight: options.strokeWeight || 2,
        fillColor: options.fillColor || '#2D8CF0',
        fillOpacity: options.fillOpacity || 0.2
      })
    } else {
      if (!globalGoogleMap || !globalGoogleMap.maps) return null
      const polygon = new globalGoogleMap.maps.Polygon({
        paths: options.path.map(p => ({ lat: Number(p.lat), lng: Number(p.lng) })),
        strokeColor: options.strokeColor || '#2D8CF0',
        strokeWeight: options.strokeWeight || 2,
        fillColor: options.fillColor || '#2D8CF0',
        fillOpacity: options.fillOpacity || 0.2
      })
      polygon.setPath = (path) => polygon.setPaths(path.map(p => ({ lat: Number(p.lat), lng: Number(p.lng) })))
      return polygon
    }
  }

  // Circle Editor
  createCircleEditor (map, circle) {
    if (this.isAmap) {
      if (!globalAMap) return null
      return new globalAMap.CircleEditor(map, circle)
    } else {
      if (!globalGoogleMap || !globalGoogleMap.maps || !globalGoogleMap.maps.event) return null
      return {
        open: () => circle.setEditable(true),
        close: () => circle.setEditable(false),
        on: (event, callback) => {
          if (event === 'adjust') {
            globalGoogleMap.maps.event.addListener(circle, 'radius_changed', () => {
              callback({ radius: circle.getRadius() })
            })
          }
          if (event === 'move') {
            globalGoogleMap.maps.event.addListener(circle, 'center_changed', () => {
              const c = circle.getCenter()
              callback({ lnglat: { lng: c.lng(), lat: c.lat() } })
            })
          }
        }
      }
    }
  }

  // MouseTool for drawing
  createMouseTool (map) {
    if (this.isAmap) {
      if (!globalAMap) return null
      return new globalAMap.MouseTool(map)
    } else {
      if (!globalGoogleMap || !globalGoogleMap.maps || !globalGoogleMap.maps.drawing) return null
      const drawingManager = new globalGoogleMap.maps.drawing.DrawingManager({
        drawingMode: null,
        drawingControl: false,
        map: map
      })
      let drawCallback = null
      globalGoogleMap.maps.event.addListener(drawingManager, 'overlaycomplete', (event) => {
        drawingManager.setDrawingMode(null)
        if (drawCallback) {
          drawCallback({ obj: event.overlay })
        }
      })
      return {
        circle: (options) => {
          drawingManager.setOptions({
            circleOptions: {
              strokeColor: options.strokeColor,
              fillColor: options.fillColor,
              fillOpacity: options.fillOpacity,
              strokeWeight: options.strokeWeight
            }
          })
          drawingManager.setDrawingMode(globalGoogleMap.maps.drawing.OverlayType.CIRCLE)
        },
        polygon: (options) => {
          drawingManager.setOptions({
            polygonOptions: {
              strokeColor: options.strokeColor,
              fillColor: options.fillColor,
              fillOpacity: options.fillOpacity,
              strokeWeight: options.strokeWeight
            }
          })
          drawingManager.setDrawingMode(globalGoogleMap.maps.drawing.OverlayType.POLYGON)
        },
        close: () => {
          drawingManager.setDrawingMode(null)
        },
        on: (event, callback) => {
          if (event === 'draw') {
            drawCallback = callback
          }
        }
      }
    }
  }

  // Geocoder
  createGeocoder () {
    if (this.isAmap) {
      if (!globalAMap) return null
      return new globalAMap.Geocoder()
    } else {
      if (!globalGoogleMap || !globalGoogleMap.maps) return null
      const geocoder = new globalGoogleMap.maps.Geocoder()
      return {
        getAddress: (lnglat, callback) => {
          let lat, lng
          if (Array.isArray(lnglat)) {
            lat = lnglat[1]
            lng = lnglat[0]
          } else {
            lat = lnglat.lat || lnglat.getLat()
            lng = lnglat.lng || lnglat.getLng()
          }
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
              callback('complete', { regeocode: { formattedAddress: results[0].formatted_address } })
            } else {
              callback('error', null)
            }
          })
        }
      }
    }
  }

  // Animation engine for path playback (moveAlong)
  moveAlong (marker, path, durationPerPoint = 200, callback = null) {
    if (this.isAmap) {
      // amap format
      marker.moveAlong(path.map(p => [p.lng, p.lat]), durationPerPoint, function (k) {
        return k
      }, false)
      if (callback) {
        marker.on('moving', callback)
      }
    } else {
      // google maps custom animation engine
      let currentIndex = 0
      let startTime = null
      let animationFrameId = null

      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp
        const progress = timestamp - startTime

        if (currentIndex < path.length - 1) {
          const startPt = path[currentIndex]
          const endPt = path[currentIndex + 1]
          const ratio = Math.min(progress / durationPerPoint, 1)

          const lat = startPt.lat + (endPt.lat - startPt.lat) * ratio
          const lng = startPt.lng + (endPt.lng - startPt.lng) * ratio

          marker.setPosition({ lat, lng })
          if (callback) callback({ passedPath: path.slice(0, currentIndex + 1) })

          if (ratio === 1) {
            currentIndex++
            startTime = null
          }
          animationFrameId = requestAnimationFrame(animate)
        }
      }

      marker.startMove = () => {
        if (!animationFrameId) animationFrameId = requestAnimationFrame(animate)
      }
      marker.pauseMove = () => {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId)
          animationFrameId = null
        }
      }
      marker.resumeMove = () => {
        if (!animationFrameId) animationFrameId = requestAnimationFrame(animate)
      }
      marker.stopMove = () => {
        if (animationFrameId) cancelAnimationFrame(animationFrameId)
        animationFrameId = null
        currentIndex = 0
        startTime = null
      }

      marker.startMove()
    }
  }

  // Helper method to uniformly set fitView
  setFitView (map, overlays) {
    if (!map || !overlays) return
    if (this.isAmap) {
      map.setFitView(overlays)
    } else {
      if (!globalGoogleMap || !globalGoogleMap.maps) return
      const bounds = new globalGoogleMap.maps.LatLngBounds()
      overlays.forEach(overlay => {
        if (!overlay) return
        if (overlay.position) {
          bounds.extend(overlay.position)
        } else if (typeof overlay.getBounds === 'function') {
          bounds.union(overlay.getBounds())
        } else if (overlay.getPath) {
          overlay.getPath().forEach(p => bounds.extend(p))
        }
      })
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds)
      }
    }
  }

  // Add overlays to map
  addOverlays (map, overlays) {
    let arr = Array.isArray(overlays) ? overlays : [overlays]
    if (this.isAmap) {
      if (!map) return
      map.add(arr)
    } else {
      arr.forEach(overlay => {
        if (!overlay) return
        if (overlay.setMap) overlay.setMap(map)
        else overlay.map = map
      })
    }
  }

  // Remove overlays from map
  removeOverlays (map, overlays) {
    if (!overlays) return
    let arr = Array.isArray(overlays) ? overlays : [overlays]
    if (this.isAmap) {
      if (!map) return
      map.remove(arr)
    } else {
      arr.forEach(overlay => {
        if (!overlay) return
        if (overlay.setMap) overlay.setMap(null)
        else overlay.map = null
      })
    }
  }
  addMapListener (map, eventName, callback) {
    if (!map) return
    if (this.isAmap) {
      map.on(eventName, (e) => {
        if (eventName === 'click') {
          callback({ lng: e.lnglat.getLng(), lat: e.lnglat.getLat() })
        } else {
          callback(e)
        }
      })
    } else {
      if (!globalGoogleMap || !globalGoogleMap.maps || !globalGoogleMap.maps.event) return
      globalGoogleMap.maps.event.addListener(map, eventName, (e) => {
        if (eventName === 'click') {
          callback({ lng: e.latLng.lng(), lat: e.latLng.lat() })
        } else {
          callback(e)
        }
      })
    }
  }

  // 辅助方法：安全注入 on 方法，不覆盖 Google Maps 内部混淆的 on 属性
  _safeDefineOn (obj, customOn) {
    if (!obj) return
    const originalOn = obj.on
    obj.on = new Proxy(customOn, {
      get (target, prop) {
        if (prop in target) {
          return target[prop]
        }
        if (originalOn) {
          const val = originalOn[prop]
          if (typeof val === 'function') {
            return val.bind(originalOn)
          }
          return val
        }
        return undefined
      },
      set (target, prop, value) {
        if (originalOn) {
          originalOn[prop] = value
          return true
        }
        target[prop] = value
        return true
      }
    })
  }
}

export default new MapAdapter()



// WEBPACK FOOTER //
// ./src/utils/mapAdapter.js